// 게임 관련 유틸리티 함수들

// 랜덤 그라디언트 색상 배열
const gradientColors = [
  'rgba(201, 34, 34, 0.8)',    // 빨간색
  'rgba(34, 120, 201, 0.8)',   // 파란색
  'rgba(34, 201, 120, 0.8)',   // 초록색
  'rgba(201, 120, 34, 0.8)',   // 주황색
  'rgba(120, 34, 201, 0.8)',   // 보라색
  'rgba(201, 34, 120, 0.8)',   // 분홍색
  'rgba(34, 201, 201, 0.8)',   // 청록색
  'rgba(201, 201, 34, 0.8)',   // 노란색
  'rgba(120, 201, 34, 0.8)',   // 연두색
  'rgba(34, 34, 201, 0.8)',    // 남색
  'rgba(150, 75, 0, 0.8)',     // 갈색
  'rgba(255, 20, 147, 0.8)',   // 딥핑크
  'rgba(0, 191, 255, 0.8)',    // 딥 스카이 블루
  'rgba(50, 205, 50, 0.8)',    // 라임 그린
  'rgba(255, 69, 0, 0.8)',     // 레드 오렌지
  'rgba(138, 43, 226, 0.8)',   // 블루 바이올렛
  'rgba(255, 215, 0, 0.8)',    // 골드
  'rgba(220, 20, 60, 0.8)',    // 크림슨
  'rgba(0, 100, 0, 0.8)',      // 다크 그린
  'rgba(25, 25, 112, 0.8)',    // 미드나이트 블루
];

// RGB 색상을 rgba로 변환하는 헬퍼 함수
const rgbToRgba = (rgbString, alpha) => {
  const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
  }
  return rgbString;
};

/**
 * 게임 ID를 기반으로 랜덤 그라디언트 색상을 생성합니다.
 * 같은 게임 ID에 대해서는 항상 같은 색상이 반환됩니다.
 * @param {string|number} gameId - 게임 ID
 * @returns {string} CSS 그라디언트 문자열
 */
export const getRandomGradient = (gameId) => {
  if (!gameId) {
    // gameId가 없는 경우 순수 랜덤 색상 반환
    const randomColor = gradientColors[Math.floor(Math.random() * gradientColors.length)];
    return `linear-gradient(to bottom, transparent 52%, ${rgbToRgba(randomColor, 0.9)} 85%)`;
  }
  
  // gameId를 기반으로 해시 값 생성 (간단한 해시 함수)
  const hash = gameId.toString().split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  // 해시값을 양수로 변환하고 색상 배열 인덱스로 사용
  const colorIndex = Math.abs(hash) % gradientColors.length;
  const selectedColor = gradientColors[colorIndex];
  
  // [2026-07-27] 그라디언트 형태 변경(운영자 지정):
  //   투명 구간 50% → 52%, 색이 짙어지는 지점 100% → 85%, 색 투명도 0.8 → 0.9.
  //   색상 자체는 기존 색 목록을 그대로 쓰고(게임마다 고정) 투명도만 올린다.
  return `linear-gradient(to bottom, transparent 52%, ${rgbToRgba(selectedColor, 0.9)} 85%)`;
};

/**
 * 커스텀 설정으로 랜덤 그라디언트를 생성합니다.
 * @param {string|number} gameId - 게임 ID
 * @param {Object} options - 그라디언트 옵션
 * @param {string} options.direction - 그라디언트 방향 ('to bottom', 'to top', 'to right', 'to left', '45deg' 등)
 * @param {number} options.startPosition - 투명 시작 위치 (0-100, 기본값: 50)
 * @param {number} options.endPosition - 색상 끝 위치 (0-100, 기본값: 100)
 * @param {number} options.opacity - 색상 투명도 (0-1, 기본값: 0.8)
 * @param {string} options.startColor - 시작 색상 (기본값: 'transparent')
 * @returns {string} CSS 그라디언트 문자열
 */
export const getCustomGradient = (gameId, options = {}) => {
  const {
    direction = 'to bottom',
    startPosition = 50,
    endPosition = 100,
    opacity = 0.8,
    startColor = 'transparent'
  } = options;

  if (!gameId) {
    const randomColor = gradientColors[Math.floor(Math.random() * gradientColors.length)];
    const adjustedColor = rgbToRgba(randomColor, opacity);
    return `linear-gradient(${direction}, ${startColor} ${startPosition}%, ${adjustedColor} ${endPosition}%)`;
  }
  
  // gameId를 기반으로 해시 값 생성
  const hash = gameId.toString().split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  const colorIndex = Math.abs(hash) % gradientColors.length;
  const selectedColor = gradientColors[colorIndex];
  const adjustedColor = rgbToRgba(selectedColor, opacity);
  
  return `linear-gradient(${direction}, ${startColor} ${startPosition}%, ${adjustedColor} ${endPosition}%)`;
};

/**
 * 다중 색상 그라디언트를 생성합니다.
 * @param {string|number} gameId - 게임 ID
 * @param {Object} options - 그라디언트 옵션
 * @param {string} options.direction - 그라디언트 방향
 * @param {number} options.colorCount - 사용할 색상 개수 (2-4, 기본값: 2)
 * @param {number} options.opacity - 색상 투명도 (기본값: 0.8)
 * @returns {string} CSS 그라디언트 문자열
 */
export const getMultiColorGradient = (gameId, options = {}) => {
  const {
    direction = 'to bottom',
    colorCount = 2,
    opacity = 0.8
  } = options;

  const count = Math.min(Math.max(colorCount, 2), 4); // 2-4개 색상으로 제한
  
  if (!gameId) {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const randomColor = gradientColors[Math.floor(Math.random() * gradientColors.length)];
      colors.push(rgbToRgba(randomColor, opacity));
    }
    
    const positions = [];
    for (let i = 0; i < count; i++) {
      const position = (100 / (count - 1)) * i;
      positions.push(`${colors[i]} ${position}%`);
    }
    
    return `linear-gradient(${direction}, transparent 0%, ${positions.join(', ')})`;
  }
  
  // gameId를 기반으로 색상 선택
  const hash = gameId.toString().split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  const colors = [];
  for (let i = 0; i < count; i++) {
    const colorIndex = (Math.abs(hash) + i * 7) % gradientColors.length; // 7을 곱해서 색상 간격 조정
    const selectedColor = gradientColors[colorIndex];
    colors.push(rgbToRgba(selectedColor, opacity));
  }
  
  const positions = [];
  for (let i = 0; i < count; i++) {
    const position = 50 + (50 / (count - 1)) * i; // 50%부터 시작
    positions.push(`${colors[i]} ${position}%`);
  }
  
  return `linear-gradient(${direction}, transparent 0%, transparent 50%, ${positions.join(', ')})`;
};

/**
 * 방사형(Radial) 그라디언트를 생성합니다.
 * @param {string|number} gameId - 게임 ID
 * @param {Object} options - 그라디언트 옵션
 * @param {string} options.shape - 모양 ('circle', 'ellipse')
 * @param {string} options.position - 위치 ('center', 'top left', 'bottom right' 등)
 * @param {number} options.opacity - 색상 투명도 (기본값: 0.6)
 * @returns {string} CSS 방사형 그라디언트 문자열
 */
export const getRadialGradient = (gameId, options = {}) => {
  const {
    shape = 'ellipse',
    position = 'center bottom',
    opacity = 0.6
  } = options;

  if (!gameId) {
    const randomColor = gradientColors[Math.floor(Math.random() * gradientColors.length)];
    const adjustedColor = rgbToRgba(randomColor, opacity);
    return `radial-gradient(${shape} at ${position}, ${adjustedColor} 0%, transparent 70%)`;
  }
  
  const hash = gameId.toString().split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  const colorIndex = Math.abs(hash) % gradientColors.length;
  const selectedColor = gradientColors[colorIndex];
  const adjustedColor = rgbToRgba(selectedColor, opacity);
  
  return `radial-gradient(${shape} at ${position}, ${adjustedColor} 0%, transparent 70%)`;
};

/**
 * 트루 랜덤 그라디언트 색상을 생성합니다.
 * 매번 호출될 때마다 다른 색상이 반환됩니다.
 * @returns {string} CSS 그라디언트 문자열
 */
export const getTrueRandomGradient = () => {
  const randomColor = gradientColors[Math.floor(Math.random() * gradientColors.length)];
  return `linear-gradient(to bottom, transparent 50%, ${randomColor} 100%)`;
};

/**
 * 게임 이름을 정규화합니다.
 * @param {object} game - 게임 객체
 * @returns {string} 정규화된 게임 이름
 */
export const getGameName = (game) => {
  return game.game_name_ko || game.game_name || game.name || '';
};

/**
 * 게임 이미지 URL을 정규화합니다.
 * @param {object} game - 게임 객체
 * @returns {string} 게임 이미지 URL
 */
export const getGameImage = (game) => {
  return game.thumbnail_url || game.image || '/img/default-game.png';
};

/**
 * 벤더명을 정규화합니다.
 * @param {object} game - 게임 객체
 * @returns {string} 벤더명
 */
export const getVendorName = (game) => {
  const vendorName = game.vendor || game.provider || '';
  return vendorName;
}; 